"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useRouter, usePathname } from "next/navigation";
import { isInterviewSessionPath } from "@/lib/paths";
import { useMemberNavVisibility } from "@/hooks/useMemberNavVisibility";
import CareerStageBadge from "./CareerStageBadge";
import NavBadge from "./NavBadge";
import {
  getMemberNavBadge,
  getVisibleMemberNavItems,
  isMemberNavItemActive,
} from "./memberNavItems";

export default function Header() {
  const { user, loading, signOut } = useAuth();
  // 表示条件（求人区分・大学連携）は1つのフックでまとめて解決する
  const navVisibility = useMemberNavVisibility();
  const router = useRouter();
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleSignOut = async () => {
    setMenuOpen(false);
    const { error } = await signOut();
    if (!error) {
      router.push("/");
    }
  };

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  // 外側クリックで閉じる（pathname 変更時もナビリンクの mousedown で閉じる）
  useEffect(() => {
    if (!menuOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [menuOpen]);

  // Escape キーで閉じる
  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  // 面接セッション中はグローバルヘッダーを隠し、面接官 AI に集中できる没入表示にする
  if (isInterviewSessionPath(pathname)) return null;

  // 主要導線の5項目は縦型サイドメニューが担う。ここはそこから外した機能の副ナビ。
  const secondaryNavItems = getVisibleMemberNavItems("header", navVisibility);

  const initial = user?.email?.charAt(0).toUpperCase() ?? "U";
  const avatarUrl = user?.user_metadata?.avatar_url;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-5">
        <div className="flex items-center justify-between h-16">
          {/* 左側。ロゴはサイドメニュー上部へ移したが、サイドメニューが出ない場面
              （モバイル全般 / 未ログイン）では戻り先が無くなるためここで出す。
              入れ物の div は常に残す。練習タイプが未設定・取得中のときに要素ごと
              消えると、justify-between の子が nav だけになりアバターが左へ寄る。 */}
          <div className="flex items-center gap-3">
            <Link
              href={user ? "/dashboard" : "/"}
              className={`flex items-center ${user ? "md:hidden" : ""}`}
            >
              <img src="/logo_menren.svg" alt="MENREN" className="h-9" />
            </Link>
            {!loading && user && <CareerStageBadge />}
          </div>

          <nav className="flex items-center gap-1 md:gap-3">
            {loading && null}
            {!loading && !user && (
              <>
                <Link
                  href="/login"
                  className="text-on-surface-variant hover:text-primary px-3 md:px-4 py-2 text-xs md:text-sm font-medium shrink-0 transition-colors"
                >
                  ログイン
                </Link>
                <Link
                  href="/signup"
                  className="bg-primary text-white px-4 md:px-5 py-2 rounded-full font-bold text-xs md:text-sm shrink-0 hover:opacity-90 transition-opacity"
                >
                  新規登録
                </Link>
              </>
            )}
            {!loading && user && (
              <>
                {/* 主要導線から外した機能の副ナビ。狭い画面には置く幅がないため
                    PC のみ表示し、モバイルではヘッダー直下の横並びナビが求人・応募状況を担う。 */}
                <div className="hidden md:flex items-center gap-1 lg:gap-3">
                  {secondaryNavItems.map((item) => {
                    const active = isMemberNavItemActive(item, pathname);
                    const badge = getMemberNavBadge(item, navVisibility);
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        {...(active
                          ? { "aria-current": "page" as const }
                          : {})}
                        className={`relative px-2 lg:px-3 py-2 text-sm font-medium transition-colors rounded-lg min-h-[44px] flex items-center ${
                          active
                            ? "text-primary"
                            : "text-on-surface-variant hover:text-primary"
                        }`}
                      >
                        {item.label}
                        {badge && (
                          <NavBadge
                            label={badge.label}
                            tone={badge.tone}
                            className="-top-0.5 -right-1 px-1.5 py-px"
                          />
                        )}
                      </Link>
                    );
                  })}
                </div>

                {/* アバター + ドロップダウンメニュー */}
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={toggleMenu}
                    aria-expanded={menuOpen}
                    aria-haspopup="true"
                    aria-label="ユーザーメニューを開く"
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-shadow ${
                      menuOpen ? "ring-2 ring-primary/30" : ""
                    }`}
                  >
                    {avatarUrl && !avatarError ? (
                      <Image
                        src={avatarUrl}
                        alt=""
                        width={32}
                        height={32}
                        className="w-8 h-8 rounded-full"
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-500 text-xs font-medium">
                          {initial}
                        </span>
                      </div>
                    )}
                  </button>

                  {menuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-48 bg-surface border border-border rounded-lg shadow-lg origin-top-right transition-all duration-150 ease-out"
                    >
                      <Link
                        href="/profile"
                        role="menuitem"
                        className="flex items-center px-4 py-3 text-sm font-medium text-on-surface hover:bg-surface-container-low min-h-[44px] rounded-t-lg"
                        onClick={() => setMenuOpen(false)}
                      >
                        プロフィール
                      </Link>
                      <div className="border-t border-border" />
                      <button
                        role="menuitem"
                        onClick={handleSignOut}
                        className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-on-surface hover:bg-surface-container-low min-h-[44px] rounded-b-lg"
                      >
                        ログアウト
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
